import { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Container, Grid } from '@mui/material';
import {
    AccountBalanceWallet,
    CreditCard,
    PostAdd,
    Contactless,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttButton, SttPage } from '../../../sporttia/all';
import { getPath } from '../../Pages';
import { formatPriceByLocale, getErrorMessage } from '../../../lib/utils';
import useGroupsService from '../../../services/GroupsService';
import SttError from '../../../components/error/SttError';
import useScsService from '../../../services/ScsService';
import constants from '../../../config/constants';

export default function BuyAbono() {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { idGroup, idSc } = useParams<{ idGroup: string; idSc: string }>();
    const groupService = useGroupsService();
    const scsService = useScsService();

    const getGroupQuery = groupService.useGetGroup(parseInt(idGroup!));

    const getScTpvConfsQuery = scsService.useGetScTpvConfs(parseInt(idSc!));

    const createGroupMemberMutation = groupService.useCreateGroupMember({
        onSuccess: (response: any) => {
            // Go to pay with credit card
            if (response.tpv) {
                navigate(getPath('tpvSwitcher', { id: response.tpv.id }));

                // Just got it - redirect to SC profile
            } else {
                navigate(getPath('scProfile', { id: idSc }));
            }
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    if (getGroupQuery.isLoading || getScTpvConfsQuery.isLoading) {
        return (
            <Container maxWidth="md">
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (getGroupQuery.isError || getScTpvConfsQuery.isError) {
        return <SttError />;
    }

    if (!getGroupQuery.data || !getScTpvConfsQuery.data) {
        return null;
    }

    const { group } = getGroupQuery.data;
    const hasZitycard = getScTpvConfsQuery.data.rows.some(
        (tpv) => tpv.bank === constants.tpvConf.banks.zitycard.name,
    );

    return (
        <Container maxWidth="md">
            <SttPage innerTitle={t('abono.buyAbono')}>
                {
                    <Box mt={2}>
                        <Box>
                            {t('labels.name')}: <b>{group.name}</b>
                            <br />
                            {'enrollmentPrice' in group && (
                                <>
                                    {t('activity.enrollment')}:{' '}
                                    <b>
                                        {formatPriceByLocale(
                                            group.enrollmentPrice,
                                            group.enrollmentShape?.currency ??
                                                undefined,
                                        )}
                                    </b>
                                </>
                            )}
                            <br />
                        </Box>
                        {group.feePrice !== undefined && group.feeInterval && (
                            <>
                                {t('activity.fee')}:{' '}
                                <b>
                                    {formatPriceByLocale(
                                        group.feePrice,
                                        group.feeShape?.currency ?? undefined,
                                    )}
                                </b>{' '}
                                / {t(group.feeInterval)}
                            </>
                        )}

                        <Box mt={2}>{group.description}</Box>

                        <Box mt={3}>
                            <Grid container spacing={3}>
                                {/* --- Paying with TPV --- */}
                                {!!group.enrollmentPrice && (
                                    <>
                                        <Grid size={{ md: 6, sm: 12 }}>
                                            <SttButton
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                caption={t('tpv.payWithTPV')}
                                                startIcon={<CreditCard />}
                                                onClick={() => {
                                                    createGroupMemberMutation.mutate(
                                                        {
                                                            id: group.id,
                                                            params: {
                                                                paymentFormSignup:
                                                                    constants
                                                                        .payment
                                                                        .paymentForms
                                                                        .tpv
                                                                        .name,
                                                            },
                                                        },
                                                    );
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={{ md: 6, sm: 12 }}>
                                            <SttButton
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                caption={t(
                                                    'payment.payWithPurse',
                                                )}
                                                startIcon={
                                                    <AccountBalanceWallet />
                                                }
                                                onClick={() => {
                                                    createGroupMemberMutation.mutate(
                                                        {
                                                            id: group.id,
                                                            params: {
                                                                paymentFormSignup:
                                                                    constants
                                                                        .payment
                                                                        .paymentForms
                                                                        .purse
                                                                        .name,
                                                            },
                                                        },
                                                    );
                                                }}
                                            />
                                        </Grid>
                                        {hasZitycard && (
                                            <Grid size={{ md: 6, sm: 12 }}>
                                                <SttButton
                                                    fullWidth
                                                    variant="contained"
                                                    color="primary"
                                                    caption={t(
                                                        'payment.payWithZitycard',
                                                    )}
                                                    startIcon={<Contactless />}
                                                    onClick={() => {
                                                        createGroupMemberMutation.mutate(
                                                            {
                                                                id: group.id,
                                                                params: {
                                                                    paymentFormSignup:
                                                                        constants
                                                                            .payment
                                                                            .paymentForms
                                                                            .zitycard
                                                                            .name,
                                                                },
                                                            },
                                                        );
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                    </>
                                )}

                                {/* --- Get it for free (because it's free) --- */}
                                {!group.enrollmentPrice && (
                                    <Grid size={{ md: 5 }}>
                                        <SttButton
                                            variant="contained"
                                            color="primary"
                                            caption={t('abono.getAbono')}
                                            startIcon={<PostAdd />}
                                            onClick={() => {
                                                createGroupMemberMutation.mutate(
                                                    { id: group.id },
                                                );
                                            }}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Box>
                }
            </SttPage>
        </Container>
    );
}
